import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import React from 'react'

import { ChipGroup, Drawer } from '@src/quince-components/src/atoms'

import { DRAWER_WIDTH, CLEAR_ALL_THRESHOLD } from '../constants/filterSection.constants'
import desktopStyles from '../menu/filterSectionWithDrawer/filterSectionWithDrawer.module.scss' // Desktop  styles
import styles from '../menu/mobileFilters/mobileFilters.module.scss' // Mobile styles

interface FilterChip {
  label: string
  filterType: string
  value: string
}

interface FilterDrawerProps {
  open: boolean
  onClose: () => void
  placement?: 'left' | 'right' | 'top' | 'bottom'
  title?: React.ReactNode
  width?: number | string
  height?: string | number
  headerClassName?: string
  bodyClassName?: string
  footerClassName?: string
  children: React.ReactNode
  selectedFilterChips?: FilterChip[]
  onChipDismiss?: (label: string) => void
  clearAllFilters?: () => void
  totalSelectedFilters: number
  noOfItems?: number
  customFooter?: React.ReactNode
  hideFooter?: boolean
  isMobile?: boolean
  clearAllButtonTestId?: string
  chipTestIdPrefix?: string
  'data-testid'?: string
  maskClosable?: boolean
  /** Optional drawer ID for scroll position persistence */
  drawerId?: string
  /** Z-index for the drawer overlay */
  zIndex?: number
  /** Whether to render the drawer using a portal (outside normal DOM hierarchy) */
  usePortal?: boolean
}

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  placement = 'left',
  title,
  width = DRAWER_WIDTH,
  height = 'auto',
  headerClassName,
  bodyClassName,
  footerClassName,
  children,
  selectedFilterChips = [],
  onChipDismiss,
  clearAllFilters,
  totalSelectedFilters,
  noOfItems = 0,
  customFooter,
  hideFooter = false,
  isMobile = true,
  clearAllButtonTestId,
  chipTestIdPrefix,
  'data-testid': testId,
  maskClosable = true,
  drawerId = 'filter-drawer',
  zIndex,
  usePortal = false,
}) => {
  const { t } = useTranslation()
  // Use the appropriate style set based on viewport
  const styleSet = isMobile ? styles : desktopStyles

  // Default title render function
  const renderDefaultTitle = () => (
    <div className={styleSet.drawerTitleContainer}>
      <div className={styleSet.drawerTitleText}>
        {t('filter')}
        {totalSelectedFilters > 0 && ` (${totalSelectedFilters})`}
      </div>
    </div>
  )

  // Footer render function
  const renderFooter = () => {
    if (hideFooter) {
      return null
    }

    if (customFooter) {
      return customFooter
    }

    return (
      <div className={classNames(styleSet.drawerFooter, footerClassName)}>
        <button
          className={classNames(
            styleSet.drawerClearAllButton,
            totalSelectedFilters === 0 ? styleSet.drawerClearAllButtonDisabled : '',
          )}
          onClick={e => {
            e.stopPropagation()
            clearAllFilters?.()
          }}
          disabled={totalSelectedFilters === 0}
          data-testid={clearAllButtonTestId}
        >
          {t('clear')}
        </button>
        <button className={styleSet.drawerCloseButton} onClick={onClose}>
          {t('view')} ({noOfItems})
        </button>
      </div>
    )
  }

  return (
    <Drawer
      placement={placement}
      open={open}
      onClose={onClose}
      title={title ?? renderDefaultTitle()}
      width={width}
      height={height}
      headerClassName={classNames(styleSet.drawerCustomHeader, headerClassName)}
      bodyClassName={classNames(styleSet.drawerBody, bodyClassName)}
      footer={renderFooter()}
      data-testid={testId}
      maskClosable={maskClosable}
      id={drawerId}
      zIndex={zIndex}
      usePortal={usePortal}
    >
      <div
        className={classNames(styleSet.drawerChipsContainer, {
          [`${styleSet.noChipsContainer}`]: selectedFilterChips.length <= 0,
        })}
      >
        {selectedFilterChips.length > 0 && onChipDismiss && (
          <>
            <ChipGroup
              chips={selectedFilterChips.map(chip => ({
                label: chip.label,
                value: chip.value,
                closeable: true,
                key: `${chip.filterType}-${chip.value}`,
                'data-testid': chipTestIdPrefix ? `${chipTestIdPrefix}-${chip.value}` : undefined,
              }))}
              onChipDismiss={onChipDismiss}
              clearAll={false}
              className={styleSet.drawerTitleChips}
              chipClassName={styleSet.drawerFilterChip}
            />
            {!isMobile && selectedFilterChips.length >= CLEAR_ALL_THRESHOLD && clearAllFilters && (
              <button className={styleSet.drawerTitleClearAll} onClick={clearAllFilters}>
                {t('clear_all_v1')}
              </button>
            )}
          </>
        )}
      </div>
      <div className={styleSet.drawerContent}>{children}</div>
    </Drawer>
  )
}
